import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { applyTheme, type Mode, type Tenant } from "@ares/tokens";
import { useTenant } from "../hooks/useTenant";

interface ThemeContextValue {
  mode: Mode;
  setMode: (mode: Mode) => void;
  toggleLightDark: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "ares.theme.mode";

function readStoredMode(): Mode | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === "light" || raw === "dark" || raw === "medic") return raw;
  return null;
}

function detectInitialMode(tenant: Tenant): Mode {
  // Salutem is forced medic — no override.
  if (tenant === "salutem") return "medic";

  // Stored user-pref always wins.
  const stored = readStoredMode();
  if (stored) return stored;

  // ARES default = DARK (Phase-2-Override per W0.7 Steve Jobs / Mathias decision 2026-04-27):
  // ARES is a power-user OS, used in cockpit-environments (Home-Office, Gym), not Apothekenlicht.
  // "Pure-Black-mit-Emerald-Glow ist nicht Präferenz — es IST die Marke."
  // System prefers-color-scheme=light is honored as user-signal, otherwise default to dark.
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
}

interface ThemeProviderProps {
  children: ReactNode;
  initialMode?: Mode;
}

export function ThemeProvider({ children, initialMode }: ThemeProviderProps) {
  const { tenant } = useTenant();
  const [mode, setModeState] = useState<Mode>(() => initialMode ?? detectInitialMode(tenant));

  // Apply tokens to :root whenever (tenant, mode) changes.
  useEffect(() => {
    applyTheme(tenant, mode);
  }, [tenant, mode]);

  // Salutem-Tenant force-overrides mode to medic.
  useEffect(() => {
    if (tenant === "salutem" && mode !== "medic") {
      setModeState("medic");
    }
  }, [tenant, mode]);

  const setMode = useCallback((next: Mode) => {
    setModeState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const toggleLightDark = useCallback(() => {
    setMode(mode === "dark" ? "light" : "dark");
  }, [mode, setMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, setMode, toggleLightDark }),
    [mode, setMode, toggleLightDark],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeContext must be used within ThemeProvider");
  return ctx;
}
